<template>
  <v-card>
    <v-card-title> {{ getPageTitle() }} </v-card-title>
    <!-- actions -->
    <v-card outlined class="mx-5 mb-5">
      <wallpapers :key="currentCategoryId" :category-id="currentCategoryId"></wallpapers>
    </v-card>
    <v-card-text class="d-flex align-center flex-wrap pb-0">
      <v-spacer></v-spacer>

      <div class="d-flex align-center flex-wrap">
        <v-btn v-if="tenant.settings.acknowledgments_enabled" color="secondary" class="mb-4 mr-4"
          :to="{ name: 'admin-pages-stats-overview', query: { category: currentCategoryId } }">
          <span>{{ $t("Lukukuittaukset") }}</span>
        </v-btn>
        <v-btn color="primary" class="mb-4" :to="{ name: 'admin-page-form', query: { category: currentCategoryId } }">
          <span>{{ $t("Uusi sivu") }}</span>
        </v-btn>
      </div>
    </v-card-text>

    <div class="ml-5">
      <draggable v-model="pages" tag="div" handle=".handle" ghost-class="ghost" @end="sort(pages)">
        <div v-for="page in pages" :key="page.id">
          <v-list-item style="border-bottom: thin solid rgba(94, 86, 105, 0.14)">
            <v-list-item-icon>
              <v-icon class="handle">
                {{ icons.mdiCursorMove }}
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>
                <v-row>
                  <v-col cols="4" class="text-truncate">
                    <router-link
                      :to="{ name: 'admin-page-form', params: { id: page.id }, query: { category: currentCategoryId } }">{{
                        page.title
                      }}</router-link>
                  </v-col>
                  <v-col cols="8">
                    <template v-if="page.group_names.length < 4">
                      <v-chip color="primary" class="mr-2 pa-2" label small v-for="g in page.group_names" :key="g"> {{
                        g
                        }}</v-chip>
                    </template>
                    <template v-else>
                      <v-chip color="secondary" class="mr-2 pa-2" label small>
                        {{ page.group_names.length }} {{ $t('ryhmää') }} </v-chip>
                    </template>
                  </v-col>
                </v-row>
              </v-list-item-title>
            </v-list-item-content>

            <v-list-item-action>

              <div class="d-flex align-center">
                <template v-if="page.require_acknowledgment && tenant.settings.acknowledgments_enabled">
                  <v-btn icon @click="showAcknowledgments(page)">
                    <v-icon>{{ icons.mdiListStatus }}</v-icon>
                  </v-btn>
                </template>
                <router-link class="mr-3" style="font-size: 16px"
                  :to="{ name: 'admin-page-form', query: { parent: page.id, category: currentCategoryId } }">
                  {{ $t("Lisää alasivu") }}
                </router-link>
                <v-icon v-if="page.published" color="success">
                  {{ icons.mdiCheckboxMarkedCircle }}
                </v-icon>
                <v-icon v-else color="error">
                  {{ icons.mdiCloseCircle }}
                </v-icon>
              </div>
            </v-list-item-action>
          </v-list-item>

          <draggable :key="'child-' + page.id" v-if="page.children.length" v-model="page.children" tag="div"
            handle=".handle" @end="sort(page.children)" ghost-class="ghost">
            <v-list-item v-for="child in page.children" :key="child.id" class="ml-5"
              style="border-bottom: thin solid rgba(94, 86, 105, 0.14)">
              <v-list-item-icon>
                <v-icon class="handle">
                  {{ icons.mdiCursorMove }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  <v-row>
                    <v-col cols="4" class="text-truncate">
                      <router-link :to="{
                        name: 'admin-page-form',
                        params: { id: child.id },
                        query: { category: currentCategoryId }
                      }">{{ child.title }}</router-link>
                    </v-col>
                    <v-col cols="8">
                      <template v-if="child.group_names.length < 4">
                        <v-chip color="primary" class="mr-2 pa-2" label small v-for="cg in child.group_names" :key="cg">
                          {{
                            cg
                          }}</v-chip>
                      </template>
                      <template v-else>
                        <v-chip color="secondary" class="mr-2 pa-2" label small>
                          {{ child.group_names.length }} {{ $t('ryhmää') }} </v-chip>
                      </template>
                    </v-col>
                  </v-row>
                </v-list-item-title>
              </v-list-item-content>

              <v-list-item-action>
                <div class="d-flex">
                  <template v-if="child.require_acknowledgment && tenant.settings.acknowledgments_enabled">
                    <v-btn icon @click="showAcknowledgments(child)">
                      <v-icon>{{ icons.mdiListStatus }}</v-icon>
                    </v-btn>
                  </template>
                  <v-icon v-if="child.published && page.published" color="success">
                    {{ icons.mdiCheckboxMarkedCircle }}</v-icon>
                  <v-icon v-else-if="child.published && !page.published" color="warning">
                    {{ icons.mdiCheckboxMarkedCircle }}</v-icon>
                  <v-icon v-else color="error">
                    {{ icons.mdiCloseCircle }}
                  </v-icon>
                </div>
              </v-list-item-action>
            </v-list-item>
          </draggable>
        </div>
      </draggable>
    </div>
    <v-dialog v-model="acknowledgmentsDialogVisible" max-width="1200px" scrollable>
      <v-card style="min-height: 75vh;">
        <v-card-text class="pa-0">
          <Acknowledgments :target-type="AcknowledgmentTypes.Page" :target-id="acknowledgmentPageId"
            :version="acknowledgmentVersion" :title="acknowledgmentPageTitle" :key="`ack-page-${acknowledgmentPageId}`">
          </Acknowledgments>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import draggable from "vuedraggable";
import { onMounted, ref, watch, computed } from "@vue/composition-api";
import { useRouter } from "@core/utils";
import adminApi from "@/api/admin";
import {
  mdiCheckboxMarkedCircle,
  mdiCloseCircle,
  mdiPlus,
  mdiCursorMove,
  mdiListStatus,
} from "@mdi/js";
import { useTranslations } from "@core/libs/i18n";
import useMenus from "@/@core/utils/useMenus";
import usePageCategories from "@/@core/utils/usePageCategories";
import useAuth from "@/useAuth";
import { i18n } from "@/plugins/i18n";
import Wallpapers from './components/wallpapers.vue';
import Acknowledgments from "@/components/acknowledgements/Acknowledgments.vue"
import AcknowledgmentTypes from "@/@core/utils/acknowledgment_types";

export default {
  components: {
    draggable,
    Wallpapers,
    Acknowledgments,
  },
  setup() {
    const pages = ref([]);
    const loading = ref(false);
    const { t } = useTranslations();
    const { tenant } = useAuth();
    const { getTenantTerm } = useMenus();
    const { pageCategories, fetchPageCategories } = usePageCategories();
    const { route } = useRouter();
    const acknowledgmentsDialogVisible = ref(false);
    const acknowledgmentPageId = ref(null);
    const acknowledgmentPageTitle = ref(null);
    const acknowledgmentVersion = ref(null);

    // Get current category from route query
    const currentCategoryId = computed(() => {
      return route.value.query.category ? Number(route.value.query.category) : null;
    });

    // Get current category object
    const currentCategory = computed(() => {
      if (!currentCategoryId.value || !pageCategories.value) return null;
      return pageCategories.value.find(c => c.id === currentCategoryId.value);
    });

    // fetch data
    const fetchPages = async () => {
      loading.value = true;

      const response = await adminApi.getPages(currentCategoryId.value);
      pages.value = response.data;

      loading.value = false;
    };

    // UnRegister on leave
    onMounted(async () => {
      await fetchPageCategories();
      fetchPages();
    });

    // Watch for category changes in route
    watch(currentCategoryId, () => {
      fetchPages();
    });

    const sort = async (collection) => {
      loading.value = true;

      const order = collection.map((p) => p.id);
      await adminApi.sortPages(order);

      fetchPages();
    };

    const getPageTitle = () => {
      // If we have a current category, use its title
      if (currentCategory.value) {
        return currentCategory.value.title;
      }

      // Fallback to tenant term or default
      const tenantTerm = getTenantTerm(
        "our_company",
        tenant.value.terms?.translations,
        i18n.locale
      );
      return tenantTerm ?? t("Meidän yritys");
    };

    const showAcknowledgments = (page) => {
      acknowledgmentPageId.value = page.id;
      acknowledgmentVersion.value = page.acknowledgment_version;
      acknowledgmentPageTitle.value = page.title;
      acknowledgmentsDialogVisible.value = true;
    }

    return {
      showAcknowledgments,
      acknowledgmentPageId,
      acknowledgmentVersion,
      acknowledgmentPageTitle,
      acknowledgmentsDialogVisible,
      tenant,
      getPageTitle,
      pages,
      AcknowledgmentTypes,
      loading,
      sort,
      currentCategoryId,
      icons: {
        mdiCheckboxMarkedCircle,
        mdiCloseCircle,
        mdiPlus,
        mdiCursorMove,
        mdiListStatus
      },
      t,
    };
  },
};
</script>
